import React, { useEffect, useState } from 'react';
import DOMPurify from 'dompurify';
import { X, Loader2 } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

/**
 * PageModal - shows a CMS page (by slug) in a small centered modal.
 * Props:
 *   slug: string (e.g. 'kullanim-kosullari', 'privacy')
 *   title: fallback title while loading
 *   onClose: () => void
 */
const PageModal = ({ slug, title, onClose }) => {
  const [page, setPage] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    // Lock body scroll while modal is open
    const originalOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => { document.body.style.overflow = originalOverflow; };
  }, []);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError(null);
    fetch(`${API_URL}/api/pages/${slug}`)
      .then(r => r.ok ? r.json() : Promise.reject())
      .then(data => { if (!cancelled) setPage(data); })
      .catch(() => { if (!cancelled) setError('Sayfa yüklenemedi'); })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [slug]);

  // Escape key closes
  useEffect(() => {
    const h = (e) => { if (e.key === 'Escape') onClose(); };
    window.addEventListener('keydown', h);
    return () => window.removeEventListener('keydown', h);
  }, [onClose]);

  return (
    <div
      className="fixed inset-0 z-[100] flex items-center justify-center p-4 bg-black/60 backdrop-blur-sm animate-fadeIn"
      onClick={onClose}
      data-testid="page-modal-overlay"
    >
      <div
        className="bg-white rounded-xl shadow-2xl w-full max-w-2xl max-h-[85vh] flex flex-col overflow-hidden"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b bg-gradient-to-r from-red-600 to-red-700 text-white">
          <h2 className="text-lg font-bold truncate pr-4" data-testid="page-modal-title">
            {page?.title || title || 'Yükleniyor...'}
          </h2>
          <button
            onClick={onClose}
            className="p-1.5 hover:bg-white/20 rounded-lg transition"
            aria-label="Kapat"
            data-testid="page-modal-close-x"
          >
            <X size={20} />
          </button>
        </div>

        {/* Body */}
        <div className="flex-1 overflow-y-auto p-6">
          {loading && (
            <div className="flex items-center justify-center py-16 text-gray-500">
              <Loader2 size={24} className="animate-spin mr-2" /> Yükleniyor...
            </div>
          )}
          {error && (
            <div className="text-center py-10 text-red-600">{error}</div>
          )}
          {!loading && !error && page && (
            <div
              className="prose prose-sm max-w-none text-gray-700 leading-relaxed"
              dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(page.content || '<p>İçerik bulunamadı.</p>') }}
            />
          )}
        </div>

        {/* Footer with close button */}
        <div className="border-t bg-gray-50 px-6 py-3 flex justify-end">
          <button
            onClick={onClose}
            data-testid="page-modal-close-btn"
            className="px-6 py-2 bg-gray-800 text-white rounded-lg hover:bg-gray-900 font-medium transition"
          >
            Kapat
          </button>
        </div>
      </div>
    </div>
  );
};

export default PageModal;
